import { escapeForRegEx, Range } from '@tiptap/core';
import { ResolvedPos } from '@tiptap/pm/model';

export interface Trigger {
	char: string;
	matchQuery: RegExp | RegExp[];
	matchWithoutTrigger: boolean;
	$position: ResolvedPos;
}

export type SuggestionMatch = {
	range: Range;
	query: string;
	text: string;
} | null;

const findBestMatch = (text: string, expressions: RegExp[]) => {
	// Returns the match with the longest length
	return expressions.reduce<RegExpMatchArray | undefined>((bestMatch, regexp) => {
		const match = Array.from(text.matchAll(regexp)).pop();

		if (!match) {
			return bestMatch;
		}

		if (!bestMatch) {
			return match;
		}

		return match[0].length > bestMatch[0].length ? match : bestMatch;
	}, undefined);
};

export function findSuggestionMatch({ char, matchQuery, matchWithoutTrigger, $position }: Trigger): SuggestionMatch {
	const escapedChar = escapeForRegEx(char);
	const matchArray = Array.isArray(matchQuery) ? matchQuery : [matchQuery];
	const expressions = matchArray.map((expression) => {
		if (matchWithoutTrigger) {
			// TODO: Find a better way to handle this
			const segments = expression.source.split('*').filter(Boolean);

			// If we're matching without a trigger, we use any triggering character from
			// the possible match regex as the trigger. The tag character will still be
			// applied to the final tag display.
			if (segments.length === 1) {
				return new RegExp(`(?:^)?${expression.source}`, 'gm');
			} else {
				return new RegExp(`(?:^)?${segments[0]}+`, 'gm');
			}
		} else {
			// 1. Optionally match the start of the string
			// 2. Negative lookbehind to ensure the match is not preceded by a word character, so you can't do "blahblah@mention.name". It must be "blahblah @mention.name"
			// 3. Match the tag character
			// 4. Match the rest of the expression, which is specific to the tag type
			return new RegExp(`(?:^)?(?<!\\w)${escapedChar}${expression.source}`, 'gm');
		}
	});

	const text = $position.nodeBefore?.isText && $position.nodeBefore.text;

	if (!text) {
		return null;
	}

	const textFrom = $position.pos - text.length;
	const match = findBestMatch(text, expressions);

	if (!match || match.input === undefined || match.index === undefined) {
		return null;
	}

	// The absolute position of the match in the document
	const from = textFrom + (match.index as number);
	const to = from + match[0].length;

	// If the $position is located within the matched substring, return that range
	if (from < $position.pos && to >= $position.pos) {
		return {
			range: {
				from,
				to,
			},
			query: matchWithoutTrigger ? match[0] : match[0].slice(char.length),
			text: match[0],
		};
	}

	return null;
}
